import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useBranding } from '../../contexts/BrandingContext';
import { Toast } from '../../components/Toast';

function normalizeDomain(value: string): string {
  return value.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/+$/, '');
}

export function WhiteLabelSettings() {
  const { user } = useAuth();
  const { branding } = useBranding();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isRemovingKey, setIsRemovingKey] = useState(false);
  const [hasResendApiKey, setHasResendApiKey] = useState(false);
  const [resendApiKeyInput, setResendApiKeyInput] = useState('');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const [form, setForm] = useState({
    wl_brand_name: '',
    wl_logo_url: '',
    wl_primary_color: '',
    wl_secondary_color: '',
    wl_custom_domain: '',
    wl_from_email: '',
    wl_from_name: '',
  });
  const [whitelabelEnabled, setWhitelabelEnabled] = useState(false);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      try {
        const { data } = await supabase
          .from('coaches')
          .select('id, whitelabel_enabled, wl_brand_name, wl_logo_url, wl_primary_color, wl_secondary_color, wl_custom_domain, wl_from_email, wl_from_name')
          .eq('user_id', user.id)
          .maybeSingle() as any;

        if (data) {
          setWhitelabelEnabled(data.whitelabel_enabled ?? false);
          const { data: keyStatus } = await (supabase as any)
            .rpc('get_whitelabel_resend_key_status', { p_coach_id: data.id });
          setHasResendApiKey(keyStatus === true);
          setForm({
            wl_brand_name: data.wl_brand_name || '',
            wl_logo_url: data.wl_logo_url || '',
            wl_primary_color: data.wl_primary_color || '',
            wl_secondary_color: data.wl_secondary_color || '',
            wl_custom_domain: normalizeDomain(data.wl_custom_domain || ''),
            wl_from_email: data.wl_from_email || '',
            wl_from_name: data.wl_from_name || '',
          });
        }
      } catch {
        // fall back to defaults
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [user]);

  const handleSave = async () => {
    if (!user) return;
    setIsSaving(true);
    setToast(null);
    try {
      if (resendApiKeyInput.trim()) {
        const { error: keyError } = await (supabase as any).rpc('save_whitelabel_resend_api_key', {
          p_coach_id: (await supabase.from('coaches').select('id').eq('user_id', user.id).maybeSingle()).data?.id,
          p_resend_api_key: resendApiKeyInput.trim(),
        });
        if (keyError) throw keyError;
        setHasResendApiKey(true);
        setResendApiKeyInput('');
      }

      const { error } = await supabase
        .from('coaches')
        .update({
          wl_brand_name: form.wl_brand_name || null,
          wl_logo_url: form.wl_logo_url || null,
          wl_primary_color: form.wl_primary_color || null,
          wl_secondary_color: form.wl_secondary_color || null,
          wl_custom_domain: normalizeDomain(form.wl_custom_domain) || null,
          wl_from_email: form.wl_from_email || null,
          wl_from_name: form.wl_from_name || null,
        } as any)
        .eq('user_id', user.id);

      if (error) throw error;
      setToast({ message: 'White-label settings saved successfully.', type: 'success' });
    } catch {
      setToast({ message: 'We could not save these settings. Please try again.', type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl border border-cream-200 p-6">
        <p className="text-sm text-chocolate-400">Loading...</p>
      </div>
    );
  }

  const removeResendApiKey = async () => {
    if (!user) return;
    setIsRemovingKey(true);
    setToast(null);
    try {
      const { data: coach } = await supabase
        .from('coaches')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();
      const { error } = await (supabase as any).rpc('save_whitelabel_resend_api_key', {
        p_coach_id: coach?.id,
        p_resend_api_key: null,
      });
      if (error) throw error;
      setHasResendApiKey(false);
      setToast({ message: 'Resend account disconnected.', type: 'success' });
    } catch {
      setToast({ message: 'We could not disconnect the Resend account. Please try again.', type: 'error' });
    } finally {
      setIsRemovingKey(false);
    }
  };

  if (!whitelabelEnabled) {
    return (
      <div className="bg-cream-50 rounded-2xl border border-cream-200 p-8 text-center">
        <div className="w-12 h-12 rounded-full bg-sage-100 flex items-center justify-center mx-auto mb-4">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6b8f71" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M2 12h20" />
            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
          </svg>
        </div>
        <h3 className="text-heading-sm text-chocolate-900 mb-2">White-Label Not Enabled</h3>
        <p className="text-text-md text-chocolate-500 max-w-md mx-auto">
          White-label branding is a premium add-on that lets you customize the logo, brand name, colors, and domain
          for your respondents. Contact your account manager to upgrade.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {toast && (
        <Toast
          isVisible={!!toast}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Brand Identity */}
      <div className="bg-white rounded-2xl border border-cream-200 p-6">
        <h3 className="text-heading-sm text-chocolate-900 mb-1">Brand Identity</h3>
        <p className="text-text-md text-chocolate-500 mb-6">
          These settings replace the default NLP OS branding for your respondents.
          Leave any field blank to use the default.
        </p>

        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-chocolate-800 mb-2">Brand Name</label>
            <input
              type="text"
              value={form.wl_brand_name}
              onChange={(e) => setForm(p => ({ ...p, wl_brand_name: e.target.value }))}
              placeholder="e.g. Your Coaching Brand"
              className="w-full px-4 py-3 rounded-xl text-sm border border-cream-300 bg-cream-50 text-chocolate-900 outline-none focus:border-sage-500"
            />
            <p className="text-xs text-chocolate-400 mt-1">Replaces "NLP OS" throughout your respondent portal and emails.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-chocolate-800 mb-2">Logo URL</label>
            <input
              type="text"
              value={form.wl_logo_url}
              onChange={(e) => setForm(p => ({ ...p, wl_logo_url: e.target.value }))}
              placeholder="https://your-domain.com/logo.png"
              className="w-full px-4 py-3 rounded-xl text-sm border border-cream-300 bg-cream-50 text-chocolate-900 outline-none focus:border-sage-500"
            />
            <p className="text-xs text-chocolate-400 mt-1">Full URL to your logo image. Recommended size: 200px wide, transparent PNG.</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-chocolate-800 mb-2">Primary Color</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={form.wl_primary_color || '#1F3D2E'}
                  onChange={(e) => setForm(p => ({ ...p, wl_primary_color: e.target.value }))}
                  className="w-12 h-12 rounded-lg border border-cream-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={form.wl_primary_color}
                  onChange={(e) => setForm(p => ({ ...p, wl_primary_color: e.target.value }))}
                  placeholder="#1F3D2E"
                  className="flex-1 px-4 py-3 rounded-xl text-sm border border-cream-300 bg-cream-50 text-chocolate-900 outline-none focus:border-sage-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-chocolate-800 mb-2">Secondary Color</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={form.wl_secondary_color || '#A69884'}
                  onChange={(e) => setForm(p => ({ ...p, wl_secondary_color: e.target.value }))}
                  className="w-12 h-12 rounded-lg border border-cream-300 cursor-pointer"
                />
                <input
                  type="text"
                  value={form.wl_secondary_color}
                  onChange={(e) => setForm(p => ({ ...p, wl_secondary_color: e.target.value }))}
                  placeholder="#A69884"
                  className="flex-1 px-4 py-3 rounded-xl text-sm border border-cream-300 bg-cream-50 text-chocolate-900 outline-none focus:border-sage-500"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Domain */}
      <div className="bg-white rounded-2xl border border-cream-200 p-6">
        <h3 className="text-heading-sm text-chocolate-900 mb-1">Custom Domain</h3>
        <p className="text-text-md text-chocolate-500 mb-6">
          Point your own domain to this platform so respondents see your branding on the login and registration pages.
        </p>
        <div>
          <label className="block text-sm font-medium text-chocolate-800 mb-2">Custom Domain</label>
          <input
            type="text"
            value={form.wl_custom_domain}
            onChange={(e) => setForm(p => ({ ...p, wl_custom_domain: e.target.value }))}
            placeholder="portal.yourcoaching.com"
            className="w-full px-4 py-3 rounded-xl text-sm border border-cream-300 bg-cream-50 text-chocolate-900 outline-none focus:border-sage-500"
          />
          <p className="text-xs text-chocolate-400 mt-1">
            Enter the domain without https://. Connect it to this published Bolt site first, then use the DNS instructions below.
          </p>
          <div className="mt-4 rounded-xl bg-cream-50 border border-cream-200 p-4 text-sm text-chocolate-600 space-y-3">
            <p><strong>For a subdomain</strong>, such as portal.yourcoaching.com, create a CNAME record with name <strong>portal</strong> and target <strong>site-dns.bolt.host</strong>.</p>
            <p><strong>For a root domain</strong>, such as yourcoaching.com, use an ALIAS, ANAME, or flattened CNAME record for <strong>@</strong> pointing to <strong>site-dns.bolt.host</strong>. Add a separate CNAME for <strong>www</strong> if needed.</p>
            <p className="text-xs text-chocolate-400">In Bolt, connect the domain under Domains &amp; Hosting. HTTPS is enabled after the DNS records are correct.</p>
          </div>
        </div>
      </div>

      {/* Email Configuration */}
      <div className="bg-white rounded-2xl border border-cream-200 p-6">
        <h3 className="text-heading-sm text-chocolate-900 mb-1">Email Configuration</h3>
        <p className="text-text-md text-chocolate-500 mb-6">
          Send welcome emails and notifications from your own email address using your own Resend account.
          Leave blank to use the default NLP OS email sender.
        </p>
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-chocolate-800 mb-2">From Email Address</label>
            <input
              type="email"
              value={form.wl_from_email}
              onChange={(e) => setForm(p => ({ ...p, wl_from_email: e.target.value }))}
              placeholder="hello@yourcoaching.com"
              className="w-full px-4 py-3 rounded-xl text-sm border border-cream-300 bg-cream-50 text-chocolate-900 outline-none focus:border-sage-500"
            />
            <p className="text-xs text-chocolate-400 mt-1">The email address respondents see when they receive notifications. This domain must be verified in your Resend account.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-chocolate-800 mb-2">From Name</label>
            <input
              type="text"
              value={form.wl_from_name}
              onChange={(e) => setForm(p => ({ ...p, wl_from_name: e.target.value }))}
              placeholder="Your Coaching Brand"
              className="w-full px-4 py-3 rounded-xl text-sm border border-cream-300 bg-cream-50 text-chocolate-900 outline-none focus:border-sage-500"
            />
            <p className="text-xs text-chocolate-400 mt-1">The name shown as the sender in emails.</p>
          </div>

          <div className="rounded-xl border border-cream-200 bg-cream-50 p-4">
            <div className="flex items-center justify-between gap-4 mb-2">
              <label className="block text-sm font-medium text-chocolate-800">Resend API Key</label>
              <span className={`text-xs font-semibold ${hasResendApiKey ? 'text-sage-700' : 'text-chocolate-400'}`}>
                {hasResendApiKey ? 'Connected' : 'Not connected'}
              </span>
            </div>
            <p className="text-xs text-chocolate-500 mb-3">Use your own Resend account so you control delivery reputation, limits, and email costs. The key is stored securely and is never shown again.</p>
            <div className="flex gap-3">
              <input
                type="password"
                value={resendApiKeyInput}
                onChange={(e) => setResendApiKeyInput(e.target.value)}
                placeholder={hasResendApiKey ? 'Enter a new key to replace it' : 're_...'}
                autoComplete="new-password"
                className="min-w-0 flex-1 px-4 py-3 rounded-xl text-sm border border-cream-300 bg-white text-chocolate-900 outline-none focus:border-sage-500"
              />
              {hasResendApiKey && (
                <button
                  type="button"
                  onClick={removeResendApiKey}
                  disabled={isRemovingKey || isSaving}
                  className="px-4 py-3 rounded-xl text-sm font-semibold text-red-700 border border-red-200 hover:bg-red-50 disabled:opacity-50"
                >
                  {isRemovingKey ? 'Removing...' : 'Remove'}
                </button>
              )}
            </div>
            <p className="text-xs text-chocolate-400 mt-2">After connecting the key, verify your sending domain in Resend and use an address from that verified domain above.</p>
          </div>
        </div>
      </div>

      {/* Preview */}
      <div className="bg-cream-50 rounded-2xl border border-cream-200 p-6">
        <h3 className="text-heading-sm text-chocolate-900 mb-1">Live Preview</h3>
        <p className="text-text-md text-chocolate-500 mb-4">How your branding will appear to respondents.</p>
        <div className="bg-white rounded-xl border border-cream-200 p-6 flex items-center gap-4">
          {form.wl_logo_url ? (
            <img src={form.wl_logo_url} alt={form.wl_brand_name || 'Brand'} className="h-12 w-auto object-contain" />
          ) : (
            <div className="h-12 w-12 rounded-full flex items-center justify-center" style={{ backgroundColor: form.wl_primary_color || '#1F3D2E' }}>
              <span className="text-white font-bold text-lg">{(form.wl_brand_name || 'N')[0]}</span>
            </div>
          )}
          <div>
            <p className="font-bold text-chocolate-900" style={{ color: form.wl_primary_color || undefined }}>
              {form.wl_brand_name || 'NLP OS'}
            </p>
            <p className="text-xs text-chocolate-400">Your Operating System For Change</p>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="px-6 py-3 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-50"
          style={{ backgroundColor: branding.primaryColor }}
        >
          {isSaving ? 'Saving...' : 'Save White-Label Settings'}
        </button>
      </div>
    </div>
  );
}
